import { Link } from 'react-router-dom'
import { RenderInlineProps } from 'slate-react'
import { Routes } from 'util/Routes'
import { TokenType, useAccountTagQuery, useOpportunityTagQuery } from 'graphql/bindings/AdminBindings'
import { resolveAccounts, resolveIssuers, resolveOpportunities } from 'util/TypeaheadTools'
import React from 'react'

export type ILabelValue = {
  label: string
  value: string
}

export type ITokenDefinition = {
  trigger: string
  name: TokenType
  queryFunction: (accountId?: string) => (query: string) => Promise<ILabelValue[]>
  tokenRenderer: React.FC<RenderInlineProps>
  showWhenEmpty?: boolean
}

const TokenWrapper = (
  Component: React.FC<{ id: string; isFocused?: boolean }>
): React.FC<RenderInlineProps> => props => {
  const id = props.node.data.get('id')
  return (
    <div {...props.attributes} style={{ display: 'inline-table' }}>
      <Component id={id} isFocused={props.isFocused} />{' '}
    </div>
  )
}

export const CompanyToken: React.FC<{ id: string; isFocused?: boolean }> = ({ id, isFocused }) => {
  const { data } = useAccountTagQuery({ variables: { id }, fetchPolicy: 'cache-first' })
  return (
    <Link
      to={Routes.issuer(id)}
      target="_blank"
      className="token"
      contentEditable={false}
      style={{
        backgroundColor: isFocused ? 'rgba(80, 60, 146, 0.8)' : 'rgb(80, 60, 146)',
      }}>
      {data?.account?.name}
    </Link>
  )
}

export const AccountToken: React.FC<{ id: string; isFocused?: boolean }> = ({ id, isFocused }) => {
  const { data } = useAccountTagQuery({ variables: { id }, fetchPolicy: 'cache-first' })
  return (
    <Link
      to={Routes.account(id)}
      target="_blank"
      className="token"
      contentEditable={false}
      style={{
        backgroundColor: isFocused ? 'rgba(10, 160, 146, 0.8)' : 'rgb(10, 160, 146)',
      }}>
      {data?.account?.name}
    </Link>
  )
}

export const OpportunityToken: React.FC<{ id: string; isFocused?: boolean }> = ({ id, isFocused }) => {
  const { data } = useOpportunityTagQuery({ variables: { id }, fetchPolicy: 'cache-first' })
  return (
    <Link
      to={Routes.opportunity(id)}
      target="_blank"
      className="token"
      contentEditable={false}
      style={{
        backgroundColor: isFocused ? 'rgba(240, 120, 126, 0.8)' : 'rgb(240, 120, 126)',
      }}>
      {data?.opportunity?.company?.name} [{data?.opportunity?.trade_intent?.type}]
    </Link>
  )
}

const accountsTokenDefinition: ITokenDefinition = {
  name: TokenType.Account,
  trigger: '@',
  queryFunction: () => resolveAccounts,
  tokenRenderer: TokenWrapper(AccountToken),
}

const issuersTokenDefinition: ITokenDefinition = {
  name: TokenType.Issuer,
  trigger: '#',
  queryFunction: () => resolveIssuers,
  tokenRenderer: TokenWrapper(CompanyToken),
}

const opportunitiesTokenDefinition: ITokenDefinition = {
  name: TokenType.Opportunity,
  trigger: '+',
  queryFunction: accountId => resolveOpportunities(accountId!),
  tokenRenderer: TokenWrapper(OpportunityToken),
  showWhenEmpty: true,
}

export const tokenDefinitions = [accountsTokenDefinition, issuersTokenDefinition, opportunitiesTokenDefinition]
